import { EventEmitter } from "events";

export default class WebBluetooth {
  weightData: number;

  connected: boolean = false;

  servicesDiscovered: boolean = false;

  notificationsEnabled: boolean = false;

  selectedDevice: any;

  connectedServer: any;

  hasWeightService: boolean = false;

  hasWeightData: boolean = false;

  hasDeviceNameService: boolean = false;

  hasDeviceNameString: boolean = false;

  weightDataStreamUuid: any = {};

  deviceNameService: any;

  dataCallback: any = null;

  supported: Boolean = !!navigator.bluetooth;

  bluetoothCapacitor: any = (<any>window).bluetoothle;

  deviceDiscovered: Boolean;

  deviceAddress: string;

  eventEmitter: EventEmitter;

  constructor(eventEmitter: EventEmitter) {
    this.deviceDiscovered = false;
    this.connected = false;
    this.deviceAddress = "";
    this.eventEmitter = eventEmitter;
    this.weightData = 0;
  }

  bluetoothConstants = {
    WEIGHT_DATA_STREAM_UUID: "0000fff4-0000-1000-8000-00805f9b34fb",
    BATTERY_LEVEL_UUID: "00002a19-0000-1000-8000-00805f9b34fb",
    DEVICE_NAME_UUID: "00002a23-0000-1000-8000-00805f9b34fb",

    SERVICE0: "0000180a-0000-1000-8000-00805f9b34fb",
    SERVICE1: 0x1800,
    SERVICE2: 0x1801,
    SERVICE3: 0x181d, // Entralpi UUID
    // SERVICE3: "0000fff0-0000-1000-8000-00805f9b34fb", // Health Scale UUID
    SERVICE4: 0x180f,
    SERVICE5: "f000ffc0-0451-4000-b000-000000000000",
    SERVICE6: "0000fff0-0000-1000-8000-00805f9b34fb",
    SERVICE7: "0000fff1-0000-1000-8000-00805f9b34fb", // Entralpi UUID
    //   SERVICE7: "0000fff4-0000-1000-8000-00805f9b34fb",

    SIMPLEPROFILE_SERV_UUID: 0x181d,
    SIMPLEPROFILE_CHAR1_UUID: 0xfff1,
    SIMPLEPROFILE_CHAR2_UUID: 0xfff2,
    SIMPLEPROFILE_SERVICE: 0x00000001,
  };

  setNotificationsStatus = (status: boolean) => {
    this.notificationsEnabled = status;
  };

  setConnectedStatus = (status: boolean) => {
    this.connected = status;
  };

  setDiscoveryStatus = (status: boolean) => {
    this.servicesDiscovered = status;
  };

  resetUI = () => {
    this.setConnectedStatus(false);
    this.setDiscoveryStatus(false);
    this.setNotificationsStatus(false);
  };

  onDisconnected = (event: Event) => {
    this.connected = false;
    this.resetUI();
    
    // Emit disconnection event to notify higher-level components
    this.eventEmitter.emit('deviceDisconnected', {
      reason: 'Device disconnected',
      timestamp: Date.now()
    });
  };

  connect(device: any) {
    return new Promise<void>((resolve, reject) => {
      if (this.connected === false) {
        device.gatt.connect().then(
          (server: any) => {
            this.selectedDevice = device;
            this.connected = server.connected;
            this.setConnectedStatus(true);
            this.connectedServer = server;
            this.selectedDevice.addEventListener(
              "gattserverdisconnected",
              this.onDisconnected
            );
            this.discoverSvcsAndChars().then(() => {
              resolve();
            });
          },
          (error: any) => {
            console.error(`ERROR: could not connect - ${error}`);
            alert(`ERROR: could not connect - ${error}`);
            this.setConnectedStatus(false);
            reject(error);
          }
        );
      } else {
        resolve();
      }
    });
  }

  discoverDevices = () =>
    new Promise<void>((resolve, reject) => {
      const options = {
        filters: [{ namePrefix: "ENTRALPI" }],
        optionalServices: [
          this.bluetoothConstants.WEIGHT_DATA_STREAM_UUID,
          0xfff1,
          this.bluetoothConstants.SERVICE7,
          this.bluetoothConstants.SERVICE0,
          this.bluetoothConstants.SERVICE1,
          this.bluetoothConstants.SERVICE2,
          this.bluetoothConstants.SERVICE3,
          this.bluetoothConstants.SERVICE4,
          this.bluetoothConstants.SERVICE5,
          this.bluetoothConstants.SERVICE6,
          this.bluetoothConstants.SIMPLEPROFILE_SERV_UUID,
          this.bluetoothConstants.SIMPLEPROFILE_CHAR1_UUID,
          this.bluetoothConstants.SIMPLEPROFILE_CHAR2_UUID,
          this.bluetoothConstants.SIMPLEPROFILE_SERVICE,
        ],
      };
      const mobileNavigatorObject: any = window.navigator;
      if (mobileNavigatorObject && mobileNavigatorObject.bluetooth) {
        // Here write your logic of mobileNavigatorObject.bluetooth.requestDevice();
        mobileNavigatorObject.bluetooth
          .requestDevice(options)
          .then((device: any) => {
            this.selectedDevice = device;
            this.connect(device).then(() => {
              resolve();
            });
          })
          .catch((error: any) => {
            alert(`ERROR: ${error}`);
            console.error(`ERROR: ${error}`);
            reject();
          });
      }
    });

  async discoverDevicesOrDisconnect(): Promise<void> {
    if (this.connected && this.selectedDevice) {
      return;
    }
    return new Promise((resolve, reject) => {
      if (!this.connected) {
        this.discoverDevices()
          .then(() => {
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        // If already connected, we should still ensure services are discovered
        if (this.servicesDiscovered) {
          resolve();
        } else {
          this.discoverSvcsAndChars().then(() => {
            resolve();
          });
        }
      }
    });
  }

  async disconnect(): Promise<void> {
    return new Promise((resolve) => {
      if (this.selectedDevice && this.selectedDevice.gatt.connected) {
        this.selectedDevice.gatt.disconnect();
        this.connected = false;
        // The onDisconnected listener will handle the cleanup
        resolve();
      } else {
        this.resetUI();
        resolve();
      }
    });
  }

  toggleWeightDataNotifications = async (dataCallback: any) =>
    new Promise<void>((resolve) => {
      if (!this.connected) {
        console.error("Error: Discover and connect to a device before using this function");
        resolve();
        return;
      }
      if (!this.servicesDiscovered) {
        console.error("Error: Service discovery has not yet completed");
        resolve();
        return;
      }
      if (!this.hasWeightService) {
        console.error("Error: The connected device does not contain the weight data service");
        resolve();
        return;
      }
      if (!this.hasWeightData) {
        console.error("Error: The connected device does not contain the weight data characteristic");
        resolve();
        return;
      }
      if (!this.notificationsEnabled) {
        this.weightDataStreamUuid
          .startNotifications()
          .then(() => {
            this.notificationsEnabled = true;
            // Store the callback for proper cleanup
            this.dataCallback = dataCallback;
            this.weightDataStreamUuid.addEventListener(
              "characteristicvaluechanged",
              this.onWeightData
            );
          })
          .catch((error: any) => {
            console.error(`WebBluetooth: Error starting notifications: ${error}`);
          });
      } else {
        this.weightDataStreamUuid
          .stopNotifications()
          .then(() => {
            this.notificationsEnabled = false;
            this.weightDataStreamUuid.removeEventListener(
              "characteristicvaluechanged",
              this.onWeightData
            );
          })
          .catch((error: any) => {
            console.error(`WebBluetooth: Error stopping notifications: ${error}`);
          });
      }
      resolve();
    });

  discoverSvcsAndChars = () =>
    new Promise<void>((resolve) => {
      try {
        this.connectedServer
          .getPrimaryService(this.bluetoothConstants.SERVICE3)
          .then(
            (services: any) => {
              services
                .getCharacteristic(this.bluetoothConstants.SERVICE7)
                .then((service: any) => {
                  this.weightDataStreamUuid = service;
                  this.hasWeightService = true;
                  this.hasWeightData = true;
                  this.setDiscoveryStatus(true);
                  this.toggleWeightDataNotifications(() => {}).then(() => {
                    resolve();
                  });
                });
            },
            (error: any) => {
              alert(`ERROR: could not connect - ${error}`);
            }
          );
      } catch (error) {
        this.connectedServer
          .getPrimaryService(this.bluetoothConstants.SERVICE3)
          .then(
            (services: any) => {
              services
                .getCharacteristic(this.bluetoothConstants.SERVICE7)
                .then((service: any) => {
                  this.weightDataStreamUuid = service;
                  this.hasWeightService = true;
                  this.hasWeightData = true;
                  this.setDiscoveryStatus(true);
                  this.toggleWeightDataNotifications(() => {});
                });
            },
            (error: any) => {
              alert(`ERROR: could not connect - ${error}`);
            }
          );
      }
    });

  readDeviceName = () => {
    // state validation
    if (!this.connected) {
      alert(
        "Error: Discover and connect to a device before using this function"
      );
      return;
    }
    if (!this.servicesDiscovered) {
      alert("Error: Service discovery has not yet completed");
      return;
    }
    if (!this.hasDeviceNameService) {
      alert(
        "Error: The connected device does not contain the device informationservice"
      );
      return;
    }
    if (!this.hasDeviceNameString) {
      alert(
        "Error: The connected device does not contain the model name stringcharacteristic"
      );
      return;
    }
    this.deviceNameService
      .readValue()
      .then((value: { buffer: Iterable<number> }) => {
        // const data = new Uint8Array(value.buffer);
        /* let device_name_string = new TextDecoder("utf-8").decode(data);
              console.log(device_name_string); */
        // document.getElementById("device_name").innerHTML = device_name_string;
      })
      .catch((error: any) => {
        alert(`Error: ${error}`);
      });
  };

  onWeightData = (event: any) => {
    try {
      const { buffer } = event.target.value;
      
      // Validate buffer size before creating DataView
      if (!buffer || buffer.byteLength < 2) {
        console.warn("WebBluetooth: Invalid data buffer size:", buffer?.byteLength);
        return;
      }
      
      const dataview = new DataView(buffer);
      
      // Validate that we can read 2 bytes at offset 0
      if (dataview.byteLength < 2) {
        console.warn("WebBluetooth: DataView too small:", dataview.byteLength);
        return;
      }
      
      const weight = dataview.getUint16(0) / 100;
      
      // Validate weight is reasonable (0-1000kg range)
      if (weight < 0 || weight > 1000) {
        console.warn("WebBluetooth: Weight out of reasonable range:", weight);
        return;
      }

      // Use the stored callback if available
      if (this.dataCallback) {
        this.dataCallback(dataview);
      }
      
      // Also emit to event emitter for backward compatibility
      this.eventEmitter.emit("weightData", weight);
    } catch (e) {
      console.error("WebBluetooth: Error processing weight data:", e);
    }
  };
}
